/**
 * PortalPermissionsPanel — Matrice manuelle des tuiles portail visibles au client.
 *
 * Refonte « Tout ou Rien » :
 *   - Plus de workflow brouillon / publication
 *   - Un seul bouton « Enregistrer et Appliquer » qui écrit directement en DB
 *   - La tuile globale `immeuble` contrôle 100 % des fonctions internes du module
 *     (cf. backend/routes/portal_immeuble.py — `_get_published_immeuble_perms`)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  User, FileText, Upload, MessageCircle, Calendar, Briefcase, ReceiptText, Home,
  FileSignature, Loader2, Save, Shield, UserCheck, UserX, Receipt, HandCoins, Bell, BookOpen, LayoutTemplate,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "@/api";
import PortalTilesPreview from "./PortalTilesPreview";

const PRESET_BASE = ["info", "documents", "envoyer", "chat", "rdv", "mandats", "factures", "notifications", "mode_emploi"];
const PRESETS = [
  { id: "fiscal",       label: "Fiscal simple", tiles: [...PRESET_BASE, "t777"],             desc: "Particulier : documents, signatures, factures, RDV, bureau à domicile" },
  { id: "proprietaire", label: "Propriétaire",  tiles: [...PRESET_BASE, "immeuble"],         desc: "Immeuble à revenu (baux, RL-31, dépenses) + base" },
  { id: "entrepreneur", label: "Entrepreneur",  tiles: [...PRESET_BASE, "travail_autonome"], desc: "Entreprise (dépenses, kilométrage, factures) + base" },
];


const TILE_META = {
  info:             { label: "Info / profil",           icon: User,          desc: "Fiche personnelle et coordonnées" },
  documents:        { label: "Documents reçus",         icon: FileText,      desc: "Fichiers envoyés par le cabinet" },
  envoyer:          { label: "Envoyer un fichier",      icon: Upload,        desc: "Téléversement portail" },
  chat:             { label: "Chat Q&R",                icon: MessageCircle, desc: "Conversation avec le cabinet" },
  rdv:              { label: "Rendez-vous",             icon: Calendar,      desc: "Prise de RDV en ligne" },
  mandats:          { label: "Mandats à signer",        icon: FileSignature, desc: "Signature électronique" },
  factures:         { label: "Factures",                icon: Receipt,       desc: "Historique de facturation client" },
  t777:             { label: "Bureau à domicile",       icon: Briefcase,     desc: "Dépenses T777" },
  travail_autonome: { label: "Entreprise",              icon: ReceiptText,   desc: "Dépenses (reçus photo), kilométrage, factures" },
  immeuble:         { label: "Immeuble à revenu",       icon: Home,          desc: "Module complet : revenus locatifs, baux, RL-31, dépenses" },
  paie:             { label: "Paie",                    icon: HandCoins,     desc: "Fiches employés, feuilles de temps, documents de paie transmis, DAS (expertise Paie requise)" },
  notifications:    { label: "Notifications",           icon: Bell,          desc: "Préférences de canaux (courriel / SMS)" },
  mode_emploi:      { label: "Mode d'emploi",           icon: BookOpen,      desc: "Guides et captures d'écran du portail" },
};

export default function PortalPermissionsPanel({ master }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMember, setActiveMember] = useState("primary");
  const [draft, setDraft] = useState({ primary: {}, secondary: {} });
  const [saving, setSaving] = useState(false);

  const masterId = master?.id;

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/masters/${masterId}/portal-permissions`, { headers: authH() });
      setData(r.data);
      // Refonte « Tout ou Rien » : on lit directement les permissions appliquées
      setDraft({
        primary: { ...(r.data.primary || {}) },
        secondary: { ...(r.data.secondary || {}) },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [masterId]);

  useEffect(() => { load(); }, [load]);

  const tiles = data?.tiles || [];
  const memberPerms = draft[activeMember] || {};
  const appliedPerms = data?.[activeMember] || {};
  const hasChanges = tiles.some((t) => !!memberPerms[t] !== !!appliedPerms[t]);

  const toggle = (tileId) => {
    setDraft((d) => ({
      ...d,
      [activeMember]: { ...d[activeMember], [tileId]: !d[activeMember]?.[tileId] },
    }));
  };

  const setAll = (value) => {
    const next = {};
    tiles.forEach((t) => { next[t] = value; });
    setDraft((d) => ({ ...d, [activeMember]: next }));
  };

  const applyPreset = (preset) => {
    const next = {};
    tiles.forEach((t) => { next[t] = preset.tiles.includes(t); });
    setDraft((d) => ({ ...d, [activeMember]: next }));
    toast.info(`Modèle « ${preset.label} » appliqué — vérifiez l'aperçu puis enregistrez`);
  };

  const copyToOtherMember = async () => {
    const otherMember = activeMember === "primary" ? "secondary" : "primary";
    const otherLabel = otherMember === "secondary" ? "conjoint" : "compte principal";
    if (!hasSecondary) return;
    if (!window.confirm(`Copier cette configuration vers le ${otherLabel} et appliquer immédiatement les deux ?`)) return;
    setSaving(true);
    try {
      // 1. Sauve le membre actuel (au cas où il y avait des modifs non enregistrées)
      await axios.put(
        `${API}/masters/${masterId}/portal-permissions`,
        { member: activeMember, permissions: memberPerms },
        { headers: authH() },
      );
      // 2. Sauve les mêmes permissions sur l'autre membre
      await axios.put(
        `${API}/masters/${masterId}/portal-permissions`,
        { member: otherMember, permissions: memberPerms },
        { headers: authH() },
      );
      toast.success(`Permissions copiées et appliquées sur les deux comptes ✓`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la copie");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/masters/${masterId}/portal-permissions`,
        { member: activeMember, permissions: memberPerms },
        { headers: authH() },
      );
      toast.success(
        `Permissions appliquées pour ${activeMember === "primary" ? "le compte principal" : "le conjoint"} ✓`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  }
  if (!data) return null;

  const hasSecondary = !!data.has_secondary;
  const enabledCount = tiles.filter((t) => memberPerms[t]).length;

  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid="masters-portal-permissions">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-slate-800 inline-flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-500" /> Permissions Portail
          </h2>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Contrôle des tuiles visibles sur le portail client. <span className="font-medium text-slate-600">Tout décoché par défaut</span> — cochez ce que le client doit voir.
          </p>
          <p className="text-[11px] text-indigo-700 mt-0.5">
            💡 « Tout ou Rien » : cocher la tuile <b>Immeuble</b> ouvre automatiquement toutes les fonctions internes (baux, RL-31, dépenses, kilométrage).
          </p>
        </div>
        <div className="text-[11px] text-slate-500 bg-slate-50 px-2.5 py-1 rounded-full">
          {enabledCount} / {tiles.length} tuile(s) cochée(s)
        </div>
      </div>

      {/* Onglets membres */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <button
          onClick={() => setActiveMember("primary")}
          className={`px-4 py-2 text-sm inline-flex items-center gap-2 border-b-2 transition ${
            activeMember === "primary"
              ? "border-indigo-600 text-indigo-700 font-semibold"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
          data-testid="masters-perms-tab-primary"
        >
          <UserCheck className="w-4 h-4" /> Compte principal
        </button>
        <button
          onClick={() => hasSecondary && setActiveMember("secondary")}
          disabled={!hasSecondary}
          className={`px-4 py-2 text-sm inline-flex items-center gap-2 border-b-2 transition ${
            activeMember === "secondary"
              ? "border-indigo-600 text-indigo-700 font-semibold"
              : "border-transparent text-slate-500 hover:text-slate-700"
          } ${!hasSecondary ? "opacity-40 cursor-not-allowed" : ""}`}
          data-testid="masters-perms-tab-secondary"
          title={hasSecondary ? "" : "Aucun secondaire lié"}
        >
          <UserX className="w-4 h-4" /> Conjoint / secondaire
        </button>
      </div>

      {activeMember === "secondary" && !master?.secondary_portal_enabled && (
        <div className="mb-3 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
          ⚠️ L'accès portail du secondaire est désactivé (voir toggle dans l'en-tête). Les tuiles cochées ici ne seront
          visibles qu'après activation.
        </div>
      )}

      {/* Modèles rapides */}
      <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="masters-perms-presets">
        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1"><LayoutTemplate className="w-3.5 h-3.5" /> Modèles :</span>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => applyPreset(p)}
            title={p.desc}
            className="text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 font-medium"
            data-testid={`masters-perms-preset-${p.id}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Actions rapides */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <button
          onClick={() => setAll(true)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
          data-testid="masters-perms-check-all"
        >
          Tout cocher
        </button>
        <button
          onClick={() => setAll(false)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
          data-testid="masters-perms-uncheck-all"
        >
          Tout décocher
        </button>
        {hasSecondary && (
          <button
            onClick={copyToOtherMember}
            disabled={saving}
            className="text-[11px] px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 disabled:opacity-40 inline-flex items-center gap-1 ml-auto"
            title="Copie cette configuration vers l'autre membre et applique immédiatement les deux"
            data-testid="masters-perms-copy-to-other"
          >
            {saving
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <span className="text-[12px]">⇄</span>}
            Appliquer aux 2 conjoints
          </button>
        )}
      </div>

      {/* Grille des tuiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {tiles.map((t) => {
          const meta = TILE_META[t] || { label: t, icon: FileText, desc: "" };
          const Icon = meta.icon;
          const on = !!memberPerms[t];
          return (
            <label
              key={t}
              className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition ${
                on
                  ? "bg-indigo-50/60 border-indigo-300"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`masters-perm-tile-${t}`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(t)}
                className="mt-0.5 rounded text-indigo-600 focus:ring-indigo-500"
                data-testid={`masters-perm-check-${t}`}
              />
              <Icon className={`w-4 h-4 mt-0.5 ${on ? "text-indigo-600" : "text-slate-400"}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${on ? "text-indigo-900" : "text-slate-700"}`}>{meta.label}</div>
                <div className="text-[10px] text-slate-500 truncate">{meta.desc}</div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Aperçu live du portail (brouillon, avant enregistrement) */}
      <PortalTilesPreview
        permissions={memberPerms}
        firstName={activeMember === "primary" ? master?.primary_user?.first_name : master?.secondary_user?.first_name}
        memberLabel={activeMember === "primary" ? "compte principal" : "conjoint"}
      />

      {/* Action unique — Enregistrer et Appliquer */}
      <div className="flex items-center justify-between gap-2 mt-4 flex-wrap pt-3 border-t border-slate-100">
        <div className="text-[11px]">
          {hasChanges ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200" data-testid="masters-perms-dirty-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
              Modifications non enregistrées
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="masters-perms-clean-indicator">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
              Permissions à jour sur le portail
            </span>
          )}
        </div>
        <button
          onClick={save}
          disabled={!hasChanges || saving}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm inline-flex items-center gap-2 disabled:opacity-40 hover:bg-emerald-700 font-semibold"
          data-testid="masters-perms-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Enregistrer et Appliquer
        </button>
      </div>
    </div>
  );
}
